import React from "react";
import { message, Tag, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import genericDomain from "./genericDomain.json";
import { stringToColor } from "./helper";
import { MdCheck, MdClose } from "react-icons/md";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
// you can call getState() to get current store state.
// @ts-ignore

export const snakeToCamel = (snakeCaseString: string): string =>
  snakeCaseString.replace(/_./g, (match) => match[1].toUpperCase());

export const copyToClipboard = (
  text,
  showMessage = true,
  successMessage = "URL copied to clipboard",
  failureMessage = "Unable to copy URL to clipboard"
) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const successful = document.execCommand("copy");
    const textMessage = successful ? successMessage : failureMessage;
    if (showMessage) {
      message.success(textMessage);
    }
  } catch (error) {
    console.error("Error copying text to clipboard:", error);
  }

  document.body.removeChild(textarea);
};

export const getSignupTenantType = (email: string) =>
  genericDomain.includes(email) ? "individual" : "organization";

export const antLoadingIcon = <LoadingOutlined style={{ fontSize: 14 }} spin />;

export const isMacintosh = () => {
  return !!navigator.platform && navigator.platform.indexOf("Mac") <= -1;
};

export const isLinux = () => {
  return !!navigator.appVersion && navigator.appVersion.indexOf("Linux") > -1;
};

export const isStealth = (email: string) => email.endsWith(".local");

export const sortUserFunc = (identityA, identityB) => {
  const identityAName = `${identityA?.first_name} ${identityA?.last_name}`
    .trim()
    .toLowerCase();
  const identityBName = `${identityB?.first_name} ${identityB?.last_name}`
    .trim()
    .toLowerCase();

  if (identityAName < identityBName) {
    return -1;
  }
  if (identityAName > identityBName) {
    return 1;
  }
  return 0;
};

export const searchIdentity = (
  identity: Identity,
  searchString: string
): boolean => {
  return (
    !searchString ||
    `${identity?.first_name || ""} ${identity?.last_name || ""}`
      .toLowerCase()
      .includes(searchString.toLowerCase()) ||
    (identity?.email || "").includes(searchString.toLowerCase()) ||
    (identity.first_name || "")
      .toLowerCase()
      .includes(searchString.toLowerCase()) ||
    (identity.last_name || "")
      .toLowerCase()
      .includes(searchString.toLowerCase()) ||
    (identity.employee_id || "")
      .toLowerCase()
      .includes(searchString.toLowerCase())
  );
};

export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
};

export const stringAvatar = (
  firstName = "",
  lastName = "",
  fontSize = "10px"
) => ({
  style: {
    background: stringToColor(`${firstName} ${lastName}`),
    fontSize,
  },
  children: `${firstName.length ? firstName[0].toUpperCase() : ""}${
    lastName.length ? lastName[0].toUpperCase() : ""
  }`,
});

export const getNamefromIdentity = (identity) =>
  `${identity?.first_name} ${identity?.last_name}`;

export const getPlatformInfoFromUserAgent = (userAgent: string) => {
  let os = "Unknown";

  if (userAgent.toLowerCase().includes("win")) {
    os = "Windows";
  } else if (userAgent.toLowerCase().includes("linux")) {
    os = "Linux";
  } else if (
    userAgent.toLowerCase().includes("mac") ||
    userAgent.toLowerCase().includes("darwin")
  ) {
    os = "Mac";
  } else if (
    userAgent.toLowerCase().includes("android") ||
    userAgent.toLowerCase().includes("dart")
  ) {
    os = "Android";
  }

  return {
    os,
    appVersion:
      userAgent.split("/").length > 0 ? userAgent.split("/")[1] : null,
  };
};

export const getCustomFieldKey = (
  customField: CustomField,
  underscore = false
) => {
  let type = customField.type.toLowerCase();

  if (customField.type === "INTEGER") {
    type = "int";
  }

  if (customField.type === "BOOLEAN") {
    type = "bool";
  }

  return `c${underscore ? "_" : ""}${type}${customField.position}`;
};

export const capitalize = (str: string) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getTableViewColumns = (
  viewFields,
  identityIdKey = "identity_id"
) => {
  const {
    user_map_data,
  }: {
    user_map_data: { [key: string]: User };

    // @ts-ignore
  } = useSelector((state) => state.users);

  const {
    team_map_data,
  }: {
    team_map_data: { [key: string]: Group };
  } =
    // @ts-ignore
    useSelector((state) => state.team);

  return viewFields.map((field) => ({
    dataIndex: field.key,
    title: field.label,
    width: field.key.includes("email")
      ? 400
      : field.key.includes("bool")
      ? 150
      : 200,
    key: field.key,
    sorter: true,
    render: (v, record) => {
      const user = user_map_data?.[record?.[identityIdKey]];
      if (!user) {
        return null;
      }
      const team = team_map_data?.[user.attached_group_id];

      if (team) {
        user.shift = user.shift || team.shift;
        user.attached_group = team;
      }

      const val = user[field.key];
      // ✅ Format created_date
      if (field.key === "created_date" && val) {
        return (
          <Tooltip title={dayjs(val).format("YYYY-MM-DD")} destroyTooltipOnHide>
            {dayjs(val).format("YYYY-MM-DD")}
          </Tooltip>
        );
      }
      if (field.key === "employee_id") {
        return (
          <Tooltip title={val} destroyTooltipOnHide>
            <Tag color="magenta">{val}</Tag>
          </Tooltip>
        );
      }
      if (field.key.includes("bool") && val !== null) {
        return val ? (
          <Tooltip title={"True"} destroyTooltipOnHide>
            <MdCheck className="text-xl text-green-500" />
          </Tooltip>
        ) : (
          <Tooltip title={"False"} destroyTooltipOnHide>
            <MdClose className="text-xl text-red-500" />
          </Tooltip>
        );
      }

      if (field.key === "attached_group_role") {
        return val === "OWNER" ? (
          <Tooltip title={"Manager"} destroyTooltipOnHide>
            {" "}
            <Tag color="gold">Manager</Tag>
          </Tooltip>
        ) : (
          <Tooltip title={"Member"} destroyTooltipOnHide>
            {" "}
            <Tag color="lime">Member</Tag>
          </Tooltip>
        );
      }

      if (field.key.includes(".")) {
        const value =
          user?.[field.key.split(".")[0]]?.[field.key.split(".")[1]];
        if (field.key.includes("email")) {
          return (
            <Tooltip title={value} destroyTooltipOnHide>
              <Tag color={field.key === "email" ? "blue" : "geekblue"}>
                {value}
              </Tag>
            </Tooltip>
          );
        }

        if (field.key.includes("attached_group")) {
          return (
            <Tooltip title={value} destroyTooltipOnHide>
              <Tag color="purple">{value}</Tag>
            </Tooltip>
          );
        }

        if (field.key.includes("designation")) {
          return (
            <Tooltip title={value} destroyTooltipOnHide>
              <Tag color="cyan">{value}</Tag>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={value} destroyTooltipOnHide>
            {value}
          </Tooltip>
        );
      }

      return (
        <Tooltip title={val && String(val)} destroyTooltipOnHide>
          {val && String(val)}
        </Tooltip>
      );
    },
  }));
};

export const getDownloadTableViewColumns = (
  viewFields: Array<ViewField>,
  user: User,
  teamData: { [key: string]: Group }
) => {
  if (!user) {
    return viewFields.map(() => null);
  }

  const team = teamData?.[user.attached_group_id];

  if (team) {
    user.shift = user.shift || team.shift;
    user.attached_group = team;
  }

  return viewFields.map((field) => {
    let value = user?.[field.key];

    if (field.key.includes(".")) {
      const keys = field.key.split(".");
      value = user?.[keys[0]]?.[keys[1]];
    }

    if (field.key === "attached_group_role") {
      return value === "OWNER" ? "Manager" : "Member";
    }

    if (typeof value === "boolean") {
      return value ? "True" : "False";
    }

    return value ? String(value) : "--";
  });
};

export const flattenTree = (items) => {
  const result = [];
  if (!items) {
    return result;
  }

  function recurse(nodes) {
    for (const node of nodes) {
      const { children, ...rest } = node;
      result.push(rest);
      if (children && children.length) {
        recurse(children);
      }
    }
  }

  recurse(items);
  return result;
};

export const containsControlCharacters = (str) => {
  // eslint-disable-next-line no-control-regex
  return /[\x00-\x1F]/.test(str);
};
